/**
 * 🎆 PyrotechnicPhoenixPanel — Soulbound universal event set (5-piece).
 *
 * Reads: GET /api/pyrotechnic-phoenix/mine
 *
 * Shows the 5 pieces (helm / chest / wings / talons / aura) with owned state,
 * per-piece stats, and the 3 progressive set bonuses (Sparkler Shell,
 * Dual Equalizer, Sovereign Phoenix Economy). Pieces drop 1:1 from the
 * 4th of July Fireworks event boxes and are auto-equipped on grant.
 */
import { useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PyrotechnicPhoenixPanel({ isOwner = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    try {
      const r = await axios.get(`${API}/pyrotechnic-phoenix/mine`);
      setData(r.data);
    } catch (_e) { /* silent — panel just won't show */ }
    finally { setLoading(false); }
  };

  useEffect(() => { refresh(); }, []);

  const grantAll = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/pyrotechnic-phoenix/admin/grant-all`);
      await refresh();
    } catch (_e) { /* toast handled by page-level */ }
    setBusy(false);
  };
  const resetAll = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/pyrotechnic-phoenix/admin/reset`);
      await refresh();
    } catch (_e) { /* silent */ }
    setBusy(false);
  };

  if (loading || !data) return null;

  const owned = data.owned_count || 0;
  const total = data.total_pieces || 5;
  const pct = Math.round((owned / total) * 100);

  return (
    <div
      className="mt-6 mb-6 rounded-2xl border border-red-500/30 bg-gradient-to-br from-[#1a0505] via-[#0A0A0A] to-[#050518] p-5 shadow-[0_0_30px_rgba(220,38,38,0.20)]"
      data-testid="pyrotechnic-phoenix-panel"
    >
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div>
          <div
            className="text-2xl font-black tracking-wide text-red-300"
            style={{ fontFamily: "Anton, sans-serif" }}
            data-testid="pyrotechnic-phoenix-title"
          >
            🎆 THE PYROTECHNIC PHOENIX
          </div>
          <div className="text-[11px] text-neutral-400 mt-1 leading-relaxed max-w-xl">
            Soulbound · 21/21 hero-compatible · Drops 1:1 from 4th of July Fireworks boxes.
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div
            className="px-3 py-1.5 rounded-full text-xs bg-red-500/15 border border-red-400/40 text-red-200 font-semibold"
            data-testid="pyro-owned-count"
          >
            {owned}/{total} pieces · {pct}%
          </div>
          <div
            className="px-3 py-1.5 rounded-full text-xs bg-neutral-800/60 border border-neutral-600/40 text-neutral-300"
            data-testid="pyro-soulbound-pill"
          >
            🔒 Soulbound
          </div>
          {isOwner && (
            <div className="flex gap-1.5 ml-1">
              <button
                onClick={grantAll}
                disabled={busy}
                className="px-2.5 py-1 rounded-md text-[10px] font-semibold bg-red-500/20 border border-red-400/50 text-red-100 hover:bg-red-500/30 disabled:opacity-50"
                data-testid="pyro-owner-grant-all"
                title="Owner-only: grant all 5 pieces for testing"
              >
                👑 Grant All
              </button>
              <button
                onClick={resetAll}
                disabled={busy}
                className="px-2.5 py-1 rounded-md text-[10px] font-semibold bg-neutral-800 border border-neutral-600 text-neutral-300 hover:bg-neutral-700 disabled:opacity-50"
                data-testid="pyro-owner-reset"
                title="Owner-only: reset pieces for testing"
              >
                Reset
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Pieces row */}
      <div className="grid grid-cols-5 gap-2 sm:gap-3 mb-4" data-testid="pyro-pieces-grid">
        {(data.pieces || []).map((p) => (
          <PieceCard key={p.id} piece={p} />
        ))}
      </div>

      {/* Set bonuses */}
      <div className="space-y-2" data-testid="pyro-bonuses">
        {(data.set_bonuses || []).map((b) => (
          <BonusRow key={b.id} bonus={b} owned={owned} />
        ))}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────── */

function PieceCard({ piece }) {
  const owned = piece.owned;
  return (
    <div
      className={
        "relative rounded-xl border p-2 sm:p-3 text-center transition-transform " +
        (owned
          ? "border-red-400/50 bg-red-500/10 hover:scale-[1.02] shadow-[0_0_15px_rgba(239,68,68,0.25)]"
          : "border-neutral-700/60 bg-neutral-900/40 opacity-60 grayscale")
      }
      data-testid={`pyro-piece-${piece.id}`}
      data-owned={owned ? "1" : "0"}
    >
      <div className="text-2xl sm:text-3xl mb-1" title={piece.visual}>{piece.icon}</div>
      <div className="text-[10px] sm:text-xs font-semibold leading-tight text-neutral-200 line-clamp-2">
        {piece.name}
      </div>
      <div className="text-[9px] uppercase tracking-wide text-neutral-500 mt-0.5">
        {piece.slot}
      </div>
      {owned && (
        <div className="mt-2 space-y-0.5">
          {Object.entries(piece.stats || {}).map(([k, v]) => (
            <div key={k} className="text-[9px] text-red-200/90">
              +{v} {k}
            </div>
          ))}
        </div>
      )}
      {!owned && (
        <div
          className="text-[9px] text-neutral-500 mt-2 italic"
          data-testid={`pyro-piece-${piece.id}-locked`}
        >
          Locked
        </div>
      )}
    </div>
  );
}

function BonusRow({ bonus, owned }) {
  const active = bonus.active;
  return (
    <div
      className={
        "rounded-lg border p-2.5 flex items-start gap-3 " +
        (active
          ? "border-red-400/40 bg-red-500/10"
          : "border-neutral-700/50 bg-neutral-900/40 opacity-70")
      }
      data-testid={`pyro-bonus-${bonus.id}`}
      data-active={active ? "1" : "0"}
    >
      <div className="text-lg">
        {active ? "🔥" : "🔒"}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-neutral-100">
          {bonus.pieces_required}-Piece · {bonus.name}
        </div>
        <div className="text-[11px] text-neutral-400 mt-0.5">{bonus.effect}</div>
        {!active && (
          <div className="text-[10px] text-neutral-500 mt-1">
            {bonus.pieces_required - owned} more piece{bonus.pieces_required - owned === 1 ? "" : "s"} to unlock.
          </div>
        )}
      </div>
    </div>
  );
}
